import React, { useEffect, useRef } from 'react';
import { Lock } from 'lucide-react';
import { BasketIcon } from '../BasketIcon';
import { usePremium } from '../Subscription/PremiumProvider';
import { isLocked } from '../../lib/premiumLock';

/**
 * Basket row control — same content-sized chip look as ConnectionsBasketChip
 * (icon + ripe count + single-line name), with selected dark state for the row.
 */
const ConnectionsBasketTile = ({
  basket,
  selected = false,
  onClick,
  scrollIntoViewOnSelect = true,
}) => {
  const { isPremium, isLoading } = usePremium();
  const buttonRef = useRef(null);
  const topics = basket?.topics || [];
  const ripeCount = topics.filter((topic) => topic.is_ripe).length;
  const total = topics.length;
  const isEmpty = total === 0;
  const showLock = isLocked(basket, isPremium, isLoading);

  useEffect(() => {
    if (!selected || !scrollIntoViewOnSelect) return;
    buttonRef.current?.scrollIntoView({
      behavior: 'smooth',
      inline: 'nearest',
      block: 'nearest',
    });
  }, [selected, scrollIntoViewOnSelect]);

  const handleClick = () => {
    onClick?.(basket);
  };

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={handleClick}
      aria-pressed={selected}
      aria-label={`Open basket ${basket?.name || ''}${isEmpty ? ' (empty)' : ''}`}
      className={`
        inline-flex items-center gap-2 shrink-0 snap-start max-w-[240px]
        px-3 py-2 rounded-xl border-2 text-left
        focus:outline-none focus:ring-2 focus:ring-cranberry-700
        ${
          selected
            ? 'border-neutral-900 bg-neutral-900 text-white hover:bg-neutral-800'
            : 'border-neutral-900 bg-white hover:bg-neutral-50'
        }
        ${!selected && isEmpty ? 'text-neutral-500' : ''}
      `}
    >
      <span
        className={`shrink-0 scale-75 [&_svg]:h-5 [&_svg]:w-5 [&_svg]:mr-0 ${
          isEmpty && !selected ? 'opacity-60' : ''
        }`}
        aria-hidden="true"
      >
        <BasketIcon />
      </span>
      <span
        className={`text-xs font-semibold tabular-nums px-1.5 py-0.5 rounded-md shrink-0 ${
          selected
            ? 'text-white bg-neutral-700'
            : isEmpty
              ? 'text-neutral-500 bg-neutral-100'
              : 'text-neutral-600 bg-neutral-100'
        }`}
      >
        {isEmpty ? 'Empty' : `${ripeCount}/${total}`}
      </span>
      <span
        className={`text-sm font-semibold truncate ${
          selected
            ? 'text-white'
            : isEmpty
              ? 'text-neutral-500'
              : 'text-neutral-900'
        }`}
      >
        {basket?.name}
      </span>
      {showLock && (
        <span
          className={`inline-flex items-center gap-0.5 shrink-0 text-[10px] font-semibold uppercase tracking-wide rounded-full px-1.5 py-0.5 border ${
            selected
              ? 'text-white bg-neutral-700 border-neutral-500'
              : 'text-neutral-500 bg-neutral-100 border-neutral-300'
          }`}
        >
          <Lock size={10} aria-hidden="true" />
          Premium
        </span>
      )}
    </button>
  );
};

export default ConnectionsBasketTile;
